/**
 * MYAC-822 — live regulation-watch label resolution for TD applied standards.
 *
 * 1:1 TypeScript port of the strict matcher in
 * assessments/src/shared/jobs/td/job_td_applied_standards_extract.py
 * (_normalize_designation / _norm_full / _pick_label_for_standard).
 * The Python job remains the reference implementation — any change there must
 * be mirrored here (and vice versa). The unit tests pin the shared behaviour,
 * including the MYAC-739 case: an entry about `EN 62304:2006/AC:2008` must NOT
 * label a TD on `EN 62304:2006+A1:2015`.
 *
 * On top of the port, one new display rule (MYAC-822): a standard with no
 * adverse report entry whose exact designation+edition IS in the tenant's
 * monitored regulations list gets the positive "up to date" label; a standard
 * unknown to the regulatory watch gets no label at all.
 */

/** Badge value for the new positive state — also a BADGE_STYLES key. */
export const UP_TO_DATE_STATUS = 'up to date'

/** Badge value for a standard the veille does not cover — also a BADGE_STYLES key. */
export const TO_BE_CHECKED_STATUS = 'to be checked'

/** Slim report-entry shape the matcher needs (subset of ReportEntry). */
export interface RegWatchMatchEntry {
  designation: string
  category: string
  badge: string
}

const ADOPTION_PREFIX_RE = /^(?:nf|en|din|bs|cen|astm|jis|csa|gb|gost)\s+/

/** Family key of a designation: edition and adoption prefixes stripped. */
export function normalizeDesignation(d: string): string {
  let s = d.toLowerCase().trim()
  s = s.replace(/\s+/g, ' ')
  s = s.replace(/\s*\(.*?\)\s*$/, '')
  s = s.replace(/:\d{4}.*$/, '')
  s = s.replace(/\s*\/\s*/g, '/')
  s = s.replace(/\s*\+\s*/g, '+')
  while (ADOPTION_PREFIX_RE.test(s)) s = s.replace(ADOPTION_PREFIX_RE, '')
  return s.trim()
}

const BODY_TOKENS = new Set([
  'nf', 'en', 'iso', 'iec', 'etsi', 'evs', 'cen', 'cenelec', 'din', 'bs', 'ansi',
])

/**
 * Comparable signature of a FULL designation+edition: lowercase, split on
 * non-alphanumerics, drop adoption/body prefixes. 'EN 62304:2006+A1:2015' and
 * 'EN 62304 2006+A1:2015' both → '62304 2006 a1 2015'; a DIFFERENT edition
 * ('EN 62304:2006/AC:2008' → '62304 2006 ac 2008') does NOT collide.
 */
export function normFull(s: string): string {
  const toks = (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(Boolean)
  return toks.filter((t) => !BODY_TOKENS.has(t)).join(' ')
}

// When the same designation appears in multiple report entries, prefer the
// most actionable category (a withdrawal is more urgent than an upcoming change).
const CATEGORY_PRIORITY: Record<string, number> = { withdrawn: 3, upcoming: 2, published: 1 }

/** designation family → entries, mirroring the job's `_build_regulation_watch_lookup` bucketing. */
export function buildEntryLookup(entries: RegWatchMatchEntry[]): Map<string, RegWatchMatchEntry[]> {
  const byNorm = new Map<string, RegWatchMatchEntry[]>()
  for (const e of entries) {
    if (!e.designation) continue
    const key = normalizeDesignation(e.designation)
    if (!key) continue
    const bucket = byNorm.get(key)
    if (bucket) bucket.push(e)
    else byNorm.set(key, [e])
  }
  return byNorm
}

/**
 * The report's status for the SAME standard EDITION, or null.
 * STRICT — mirror the regulation-watch report, never infer or invent a label.
 */
export function pickLabelForStandard(
  entries: RegWatchMatchEntry[],
  tdFull: string,
): string | null {
  if (!entries.length || !tdFull) return null
  const matches = entries.filter((e) => normFull(e.designation) === tdFull)
  if (!matches.length) return null
  const best = matches.reduce((a, b) =>
    (CATEGORY_PRIORITY[b.category] ?? 0) > (CATEGORY_PRIORITY[a.category] ?? 0) ? b : a,
  )
  return best.badge.trim() || best.category || null
}

/**
 * Matcher outcome for one standard:
 * 1. adverse/pending entry matching the exact designation+edition → its badge;
 * 2. else exact designation+edition present in the monitored regulations list
 *    → "up to date" (green);
 * 3. else → null (the regulatory watch knows nothing about this standard).
 */
export function resolveStandardStatus(
  designation: string,
  edition: string | null | undefined,
  entryLookup: Map<string, RegWatchMatchEntry[]>,
  monitoredFulls: Set<string>,
): string | null {
  const family = normalizeDesignation(designation)
  if (!family) return null
  const tdFull = normFull(`${designation} ${edition ?? ''}`)
  if (!tdFull) return null
  const label = pickLabelForStandard(entryLookup.get(family) ?? [], tdFull)
  if (label) return label
  return monitoredFulls.has(tdFull) ? UP_TO_DATE_STATUS : null
}

/**
 * Full MYAC-822 display rule: the matcher outcome, with the null case surfaced
 * as a neutral "to be checked" tag — but ONLY when the tenant actually has a
 * veille report (`hasReport`). Without one, tagging every standard "to be
 * checked" would just be noise for tenants not using the regulatory watch.
 */
export function displayStandardStatus(
  designation: string,
  edition: string | null | undefined,
  entryLookup: Map<string, RegWatchMatchEntry[]>,
  monitoredFulls: Set<string>,
  hasReport: boolean,
): string | null {
  const status = resolveStandardStatus(designation, edition, entryLookup, monitoredFulls)
  if (status) return status
  return hasReport ? TO_BE_CHECKED_STATUS : null
}
